
import { useConnectionTester } from "./useConnectionTester";
import { useConnectionManager } from "./useConnectionManager";
import { logger } from "@/lib/logger";

/**
 * Combines connection testing and management functionality
 */
export const useSlackConnector = (
  aiStaffId: string,
  setIsConnected: (connected: boolean) => void,
  setConnectionError: (error: string | null) => void,
  setIsLoading: (loading: boolean) => void
) => {
  const { testConnection, isTestingConnection } = useConnectionTester();
  
  const { handleConnect, handleDisconnect } = useConnectionManager(
    aiStaffId,
    setIsConnected,
    setConnectionError,
    setIsLoading
  );
  
  const testAndConnect = async (webhookUrl: string, slackToken: string) => {
    try {
      logger.info({
        message: "Testing and connecting to Slack",
        context: { aiStaffId }
      });
      
      setIsLoading(true);
      
      // First test the connection
      logger.info({
        message: "Testing connection before connecting",
        context: { aiStaffId }
      });
      
      const testSuccess = await testConnection(webhookUrl, slackToken);
      if (!testSuccess) {
        setConnectionError("Connection test failed. Please check your credentials and try again.");
        return false;
      }
      
      logger.info({
        message: "Test successful, proceeding with connection",
        context: { aiStaffId }
      });
      
      // If test succeeds, proceed with connection
      return await handleConnect(webhookUrl, slackToken);
    } catch (error) {
      logger.error({
        message: "Error in testAndConnect",
        error: error instanceof Error ? error : new Error(String(error)),
        context: { aiStaffId }
      });
      
      const errorMessage = error instanceof Error 
        ? error.message 
        : "Unknown error connecting to Slack";
      
      setConnectionError(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };
  
  return {
    testConnection,
    handleConnect,
    handleDisconnect,
    testAndConnect,
    isTestingConnection
  };
};
